import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Send, Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { api } from "../lib/api";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data: pubSettings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => api.get("/public-settings").then((r) => r.data),
    staleTime: 60_000,
  });
  const { data: cats } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get("/categories").then((r) => r.data),
    staleTime: 60 * 60_000,
  });
  const channel = pubSettings?.telegram_channel || "@BrickDailyDeals";
  const categories = cats || [];

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/?search=${encodeURIComponent(q.trim())}`);
  };

  const onCatChange = (k) => {
    navigate(k === "all" ? "/" : `/categoria/${k}`);
    setOpen(false);
  };

  const params = new URLSearchParams(location.search);
  // Active category from either dedicated route /categoria/:key or ?category= query string
  const pathCat = location.pathname.startsWith("/categoria/")
    ? location.pathname.split("/")[2]
    : null;
  const activeCat = pathCat || params.get("category") || "all";
  const activeLabel =
    activeCat === "all"
      ? "Tutte le categorie"
      : categories.find((c) => c.key === activeCat)?.label || "Categoria";

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/75 border-b border-zinc-900"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center gap-3 sm:gap-4">
          <Logo />

          {/* Categories dropdown (replaces tab nav) */}
          <div className="hidden lg:block relative ml-4">
            <select
              data-testid="category-select"
              value={activeCat}
              onChange={(e) => onCatChange(e.target.value)}
              className="appearance-none bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-sm font-bold rounded-full pl-4 pr-9 py-2 outline-none focus:border-[#FBD51C]/60 cursor-pointer min-w-[200px]"
            >
              <option value="all">Tutte le categorie</option>
              {categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FBD51C] pointer-events-none" />
          </div>

          <div className="flex-1" />

          <form
            onSubmit={onSearch}
            className="hidden md:flex items-center gap-2 bg-[#131e3a] border border-[#1f2c4a] rounded-full pl-4 pr-1 py-1 w-64 focus-within:border-[#FBD51C]/60 transition-colors"
          >
            <Search size={16} className="text-zinc-500" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca un set..."
              className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-500 flex-1 py-1.5"
            />
            <button
              type="submit"
              data-testid="search-submit"
              className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold rounded-full px-3 py-1.5"
            >
              Vai
            </button>
          </form>

          <a
            href={`https://t.me/${(channel || "").replace("@", "")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-telegram-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-[#229ED9] hover:bg-[#1c8acb] text-white font-bold text-sm rounded-full px-4 py-2 transition-colors"
          >
            <Send size={14} />
            <span className="hidden md:inline">Iscriviti</span>
            <span className="md:hidden">Telegram</span>
          </a>

          <Link
            to="/guide"
            data-testid="header-guide-link"
            className="hidden lg:inline-flex items-center gap-2 text-sm font-bold text-zinc-200 hover:text-[#FBD51C] transition-colors px-2"
          >
            Guide
          </Link>
          <Link
            to="/chi-siamo"
            data-testid="header-chi-siamo-link"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-bold text-zinc-200 hover:text-[#FBD51C] transition-colors px-2"
          >
            Chi siamo
          </Link>
          <Link
            to="/contatti"
            data-testid="header-contatti-link"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-bold text-zinc-200 hover:text-[#FBD51C] transition-colors px-2"
          >
            Contatti
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setOpen(!open)}
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4 space-y-3" data-testid="mobile-menu">
            <form
              onSubmit={onSearch}
              className="flex items-center gap-2 bg-[#131e3a] border border-[#1f2c4a] rounded-full pl-4 pr-1 py-1"
            >
              <Search size={16} className="text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca un set..."
                className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-500 flex-1 py-1.5"
              />
              <button
                type="submit"
                className="bg-zinc-800 text-white text-xs font-bold rounded-full px-3 py-1.5"
              >
                Vai
              </button>
            </form>
            <div className="relative">
              <select
                data-testid="category-select-mobile"
                value={activeCat}
                onChange={(e) => onCatChange(e.target.value)}
                className="w-full appearance-none bg-zinc-900 border border-zinc-800 text-white text-sm font-bold rounded-lg pl-4 pr-9 py-3 outline-none focus:border-[#FBD51C]/60"
              >
                <option value="all">Tutte le categorie</option>
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#FBD51C] pointer-events-none" />
            </div>
            <Link
              to="/guide"
              data-testid="mobile-guide-link"
              onClick={() => setOpen(false)}
              className="block bg-zinc-900 border border-zinc-800 text-white text-sm font-bold rounded-lg px-4 py-3 hover:border-[#FBD51C]/60"
            >
              📖 Guide e News
            </Link>
            <div className="grid grid-cols-3 gap-2">
              <Link
                to="/chi-siamo"
                data-testid="mobile-chi-siamo-link"
                onClick={() => setOpen(false)}
                className="block text-center bg-zinc-900 border border-zinc-800 text-white text-xs font-bold rounded-lg px-2 py-2.5 hover:border-[#FBD51C]/60"
              >
                Chi siamo
              </Link>
              <Link
                to="/contatti"
                data-testid="mobile-contatti-link"
                onClick={() => setOpen(false)}
                className="block text-center bg-zinc-900 border border-zinc-800 text-white text-xs font-bold rounded-lg px-2 py-2.5 hover:border-[#FBD51C]/60"
              >
                Contatti
              </Link>
              <Link
                to="/termini"
                data-testid="mobile-termini-link"
                onClick={() => setOpen(false)}
                className="block text-center bg-zinc-900 border border-zinc-800 text-white text-xs font-bold rounded-lg px-2 py-2.5 hover:border-[#FBD51C]/60"
              >
                Termini
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
